import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiError, LegacyKuduResponseEnvelope } from '@/api/client.types';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';

import {
  courseQueryKey,
  type CourseSettings,
  type CourseStaffMember,
  type RawCourse,
  type RawQuestionSettings,
} from './useCourse';

export interface CourseControlPanelUpdatePayload {
  course_question_settings?: RawQuestionSettings;
  course_settings?: CourseSettings;
  is_complete?: boolean;
  is_live?: boolean;
  new_assistant_id?: string;
  new_secondary_instructor_id?: string;
}

interface UpdateCourseControlPanelRequest extends CourseControlPanelUpdatePayload {
  id: string;
}

interface RawUpdateCourseControlPanelResponse {
  course?: RawCourse | null;
}

interface RawSearchUserProfile {
  display_name?: string | null;
  displayName?: string | null;
  first_name?: string | null;
  firstName?: string | null;
  is_instructor?: boolean | null;
  isInstructor?: boolean | null;
  last_name?: string | null;
  lastName?: string | null;
  name?: string | null;
  preferred_email?: string | null;
  preferredEmail?: string | null;
  preferred_username?: string | null;
  preferredUsername?: string | null;
}

interface RawSearchUser extends RawSearchUserProfile {
  email?: string | null;
  id?: string | null;
  profile?: RawSearchUserProfile | null;
  uid?: string | null;
}

interface SearchUsersRequest {
  restrict_to_instructors: boolean;
  user_email: string;
}

interface SearchUsersResponse {
  users?: RawSearchUser[] | null;
}

export interface CourseSearchUser extends CourseStaffMember {
  isInstructor?: boolean;
  uid?: string;
}

export interface CourseControlPanelActions {
  currentUserId: string | null;
  isSearching: boolean;
  isUpdating: boolean;
  searchError: ApiError | null;
  searchUsers(email: string): Promise<CourseSearchUser[]>;
  updateCourse(payload: CourseControlPanelUpdatePayload): Promise<unknown>;
  updateError: ApiError | null;
}

function createDataError(message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  return error;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function getProfile(user: RawSearchUser): RawSearchUserProfile {
  return isRecord(user.profile) ? user.profile : {};
}

function getProfileString(
  user: RawSearchUser,
  snakeKey: keyof RawSearchUserProfile,
  camelKey: keyof RawSearchUserProfile,
): string | null {
  const profile = getProfile(user);

  return (
    normalizeString(user[snakeKey]) ??
    normalizeString(user[camelKey]) ??
    normalizeString(profile[snakeKey]) ??
    normalizeString(profile[camelKey])
  );
}

function normalizeSearchUser(user: RawSearchUser): CourseSearchUser | null {
  const id =
    normalizeString(user.id) ??
    normalizeString(user.uid) ??
    getProfileString(user, 'preferred_email', 'preferredEmail') ??
    normalizeString(user.email);

  if (!id) {
    return null;
  }

  const email =
    getProfileString(user, 'preferred_email', 'preferredEmail') ??
    normalizeString(user.email);
  const firstName = getProfileString(user, 'first_name', 'firstName');
  const lastName = getProfileString(user, 'last_name', 'lastName');
  const name =
    getProfileString(user, 'display_name', 'displayName') ??
    getProfileString(user, 'name', 'name') ??
    (firstName && lastName ? `${firstName} ${lastName}` : null) ??
    firstName ??
    lastName ??
    email ??
    id;
  const profile = getProfile(user);
  const uid = normalizeString(user.uid);
  const isInstructor =
    typeof user.is_instructor === 'boolean'
      ? user.is_instructor
      : typeof user.isInstructor === 'boolean'
        ? user.isInstructor
        : typeof profile.is_instructor === 'boolean'
          ? profile.is_instructor
          : typeof profile.isInstructor === 'boolean'
            ? profile.isInstructor
            : undefined;

  return {
    ...(email ? { email } : {}),
    id,
    ...(typeof isInstructor === 'boolean' ? { isInstructor } : {}),
    name,
    ...(uid ? { uid } : {}),
  };
}

function unwrapSearchUsersResponse(
  response:
    | SearchUsersResponse
    | LegacyKuduResponseEnvelope<SearchUsersResponse>
    | null
    | undefined,
): SearchUsersResponse {
  if (response && typeof response === 'object' && 'Response' in response) {
    return unwrapSearchUsersResponse(response.Response as SearchUsersResponse);
  }

  return response ?? {};
}

export function normalizeSearchUsersResponse(
  response:
    | SearchUsersResponse
    | LegacyKuduResponseEnvelope<SearchUsersResponse>
    | null
    | undefined,
): CourseSearchUser[] {
  const payload = unwrapSearchUsersResponse(response);

  return Array.isArray(payload.users)
    ? payload.users
        .filter(isRecord)
        .map((user) => normalizeSearchUser(user as RawSearchUser))
        .filter((user): user is CourseSearchUser => user !== null)
    : [];
}

export function useCourseControlPanelActions(
  courseId: string | undefined,
): CourseControlPanelActions {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getIdToken, user } = useAuth();

  const createClient = () => {
    if (!user) {
      throw createDataError('You must be signed in to manage course settings.');
    }

    const returnTo = `${location.pathname}${location.search}${location.hash}`;

    return createApiClient({
      baseUrl: import.meta.env.VITE_API_BASE_URL || '',
      getIdToken,
      getLegacyToken: async () => createLegacyKuduToken(user),
      onAuthError: () =>
        navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
          replace: true,
        }),
    });
  };

  const updateMutation = useMutation<
    RawUpdateCourseControlPanelResponse | LegacyKuduResponseEnvelope<RawUpdateCourseControlPanelResponse>,
    ApiError,
    CourseControlPanelUpdatePayload
  >({
    mutationFn: async (payload) => {
      if (!courseId) {
        throw createDataError('Course ID is required to manage this course.');
      }

      const client = createClient();

      // A grant (new_secondary_instructor_id or new_assistant_id) is completed
      // on the server, which also adds the course to the new staff member's
      // own list (issue #58). No second call for them: it would act on someone
      // else's account, and it put an assistant without an instructor profile
      // on the roster as a student.
      return client.invokeKuduMethod<
        | RawUpdateCourseControlPanelResponse
        | LegacyKuduResponseEnvelope<RawUpdateCourseControlPanelResponse>,
        UpdateCourseControlPanelRequest
      >('UpdateCourse', {
        ...payload,
        id: courseId,
      });
    },
    onSuccess: async () => {
      if (!courseId) {
        return;
      }

      await queryClient.invalidateQueries({
        queryKey: courseQueryKey(courseId),
      });
    },
  });

  const searchMutation = useMutation<CourseSearchUser[], ApiError, string>({
    mutationFn: async (email) => {
      const normalizedEmail = normalizeString(email);

      if (!normalizedEmail) {
        throw createDataError('Enter an email address to search.');
      }

      const response = await createClient().invokeKuduMethod<
        SearchUsersResponse | LegacyKuduResponseEnvelope<SearchUsersResponse>,
        SearchUsersRequest
      >('SearchUsers', {
        restrict_to_instructors: true,
        user_email: normalizedEmail,
      });

      return normalizeSearchUsersResponse(response);
    },
  });

  return {
    currentUserId: getKuduUserId(user) ?? user?.uid ?? null,
    isSearching: searchMutation.isPending,
    isUpdating: updateMutation.isPending,
    searchError: searchMutation.error,
    searchUsers: searchMutation.mutateAsync,
    updateCourse: updateMutation.mutateAsync,
    updateError: updateMutation.error,
  };
}
